import React, { useEffect } from "react";
import styles from "../../css/heatmaps/HeatmapPreviewModal.module.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { lockBodyScroll, unlockBodyScroll } from "../../utils/bodyScrollLock";
import ButtonBtn from "../buttons/ButtonBtn";
import { ReactSVG } from "react-svg";
import downloadIcon from "../../img/icons/download.svg";
import CloseBtn from "../CloseBtn";
import { generateHeatmapPdf } from "../../utils/generateHeatmapPdf";

const HeatmapPreviewModal = ({
  onClose,
  imageSrc,
  title,
  markdownText,
  // Optional: directly render a prepared React node (e.g., JSON-to-JSX analysis)
  analysisNode,
  // Optional: raw structured AI payload for PDF generation
  analysisPayload,
  bgOpacity = 0.8,
  primaryAction, // { label?: string, icon?: React.ReactNode, ariaLabel?: string, title?: string, onClick: function, disabled?: boolean }
  score, // optional: { val: number, max: number }
}) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    lockBodyScroll();
    return () => {
      window.removeEventListener("keydown", onKey);
      unlockBodyScroll();
    };
  }, [onClose]);

  // Parse score (val/max) from markdown text, tolerant to formatting variations
  const parseScore = (input) => {
    if (!input) return { scoreVal: null, scoreMax: null };
    if (typeof input === "string") {
      const lineRegex =
        /Celkové\s+hodnocení[^:]*:\s*(?:\*\*|__)?\s*(\d{1,3})\s*[\/⁄]\s*(\d{1,3})/i;
      const m =
        input.match(lineRegex) ||
        input.match(/\b(\d{1,3})\s*[\/⁄]\s*(\d{1,3})\b/);
      if (m) {
        const val = parseInt(m[1], 10);
        const max = parseInt(m[2], 10) || 10;
        if (!isNaN(val) && !isNaN(max) && max > 0)
          return { scoreVal: val, scoreMax: max };
      }
    }
    return { scoreVal: null, scoreMax: null };
  };

  const normalizeMarkdownHeadings = (text) => {
    if (!text || typeof text !== "string") return text;
    return text.replace(/^(#{1,6})([^#\s])(.*)$/gm, (m, hashes, ch, rest) => {
      return `${hashes} ${ch}${rest}`;
    });
  };

  return (
    <div
      className={styles.overlay}
      style={{ "--bg-opacity": bgOpacity }}
      onClick={onClose}
    >
      <CloseBtn />
      <div
        className={styles.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className={styles.header}>
          {title ? <h3 className={styles.title}>{title}</h3> : <div />}
          <div className={styles.headerActions}>
            {(() => {
              let scoreVal = null;
              let scoreMax = null;
              if (
                score &&
                typeof score.val === "number" &&
                typeof score.max === "number"
              ) {
                scoreVal = score.val;
                scoreMax = score.max;
              } else {
                const parsed = parseScore(markdownText);
                scoreVal = parsed.scoreVal;
                scoreMax = parsed.scoreMax;
              }
              if (typeof scoreVal !== "number" || typeof scoreMax !== "number")
                return null;
              return (
                <div className={styles.scoreBadge} aria-label="Skóre banneru">
                  <span className={styles.scoreLabel}>Skóre:</span>
                  <span className={styles.scoreValue}>{scoreVal}</span>
                  <span className={styles.scoreSlash}>/</span>
                  <span className={styles.scoreTotal}>{scoreMax}</span>
                </div>
              );
            })()}
            {imageSrc ? (
              <button
                type="button"
                className={styles.downloadBtn}
                onClick={async () => {
                  try {
                    // Determine if we have AI analysis data for PDF generation
                    const hasAiPayload =
                      analysisPayload &&
                      typeof analysisPayload === "object" &&
                      !analysisPayload._error &&
                      !analysisPayload.error &&
                      (analysisPayload.typ ||
                        analysisPayload.analyza ||
                        analysisPayload.doporuceni ||
                        analysisPayload.hodnoceni ||
                        analysisPayload.permoniq_doporuceni ||
                        (Array.isArray(analysisPayload) &&
                          analysisPayload.length > 0));

                    if (hasAiPayload) {
                      // Extract base64 from imageSrc data URI
                      let b64 = imageSrc || "";
                      if (b64.startsWith("data:")) {
                        b64 = b64.split(",")[1] || "";
                      }
                      const nameBase = (title || "obrazek")
                        .toString()
                        .replace(/\s+/g, "_");
                      await generateHeatmapPdf({
                        imageBase64: b64,
                        imageName: title || "heatmapa",
                        analysisPayload,
                        score:
                          score &&
                          typeof score.val === "number" &&
                          typeof score.max === "number"
                            ? score
                            : null,
                        fileName: `heatmap_${nameBase}`,
                      });
                    } else {
                      // Fallback: download as plain PNG
                      const a = document.createElement("a");
                      a.href = imageSrc;
                      const nameBase = (title || "obrazek")
                        .toString()
                        .replace(/\s+/g, "_");
                      a.download = `${nameBase}.png`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }
                  } catch (e) {
                    // no-op
                  }
                }}
                title={analysisPayload ? "Uložit PDF report" : "Uložit obrázek"}
                aria-label={
                  analysisPayload ? "Uložit PDF report" : "Uložit obrázek"
                }
              >
                <ReactSVG src={downloadIcon} />
              </button>
            ) : null}
          </div>
        </div>

        <div className={styles.imageWrap}>
          {imageSrc ? <img src={imageSrc} alt={title || "Náhled"} /> : null}
        </div>

        {analysisNode ? (
          <div className={styles.aiMessageBox}>{analysisNode}</div>
        ) : markdownText ? (
          <div className={styles.aiMessageBox}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                a: ({ node, ...props }) => (
                  <a {...props} target="_blank" rel="noopener noreferrer" />
                ),
              }}
            >
              {normalizeMarkdownHeadings(markdownText)}
            </ReactMarkdown>
          </div>
        ) : null}

        <div className={styles.actions}>
          {/* <button type="button" className={`btn delete`} onClick={onClose}>
            Zavřít
          </button> */}
          {primaryAction && (
            <ButtonBtn
              className={styles.aiCtaBtn}
              marginBlock="0"
              justifyContent="flex-end"
              onClick={primaryAction.onClick}
              disabled={primaryAction.disabled}
              aria-label={
                primaryAction.ariaLabel || primaryAction.title || "Akce"
              }
              title={
                primaryAction.title || primaryAction.ariaLabel || undefined
              }
            >
              {primaryAction.icon ? (
                <>
                  {primaryAction.icon}
                  <span>Zpracuj AI analýzu</span>
                </>
              ) : (
                primaryAction.label
              )}
            </ButtonBtn>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeatmapPreviewModal;
